import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpserviceService } from './httpservice.service';

declare var OneSignal: any;

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private deviceRegistered$ = new BehaviorSubject<boolean>(false);
  public deviceRegistered = this.deviceRegistered$.asObservable();

  private playerId: string | null = null;
  private readonly allowedOrigin = 'https://freshia.tropicales.e-botics.com';

  constructor(
    private http: HttpClient,
    private httpService: HttpserviceService
  ) {
    this.initializeOneSignal();
  }

/**
   * Inicializa OneSignal SDK
   */
  private initializeOneSignal(): void {
    if (!this.canInitializeOneSignal()) {
      return;
    }

    const w = window as any;
    w.OneSignal = w.OneSignal || [];
    const OneSignal = w.OneSignal;

    OneSignal.push(() => {
      OneSignal.init({
        appId: '385fe3cd-84ab-4511-9e86-10840db09c3d',
        allowLocalhostAsSecureOrigin: true,
      });

      // Obtener player ID cuando esté disponible
      OneSignal.on('subscriptionChange', (isSubscribed: boolean) => {
        console.log('OneSignal subscription changed:', isSubscribed);
        if (isSubscribed) {
          OneSignal.getUserId((userId: string) => {
            this.playerId = userId;
            console.log('OneSignal Player ID:', this.playerId);
            this.updateDeviceStatus();
          });
        }
      });

      // Obtener player ID inicial
      OneSignal.getUserId((userId: string) => {
        this.playerId = userId;
        console.log('OneSignal Player ID (initial):', this.playerId);
        this.updateDeviceStatus();
      });
    });
  }

/**
   * Actualiza el estado del dispositivo cuando obtenemos playerId.
   * Por ahora solo dejamos un log; el estado registrado se sincroniza
   * explícitamente desde el header usando checkDeviceRegistration(userId).
   */
  private updateDeviceStatus(): void {
    if (this.playerId) {
      console.log('OneSignal updateDeviceStatus: playerId disponible', this.playerId);
    }
  }


  private canInitializeOneSignal(): boolean {
    return typeof window !== 'undefined' && window.location.origin === this.allowedOrigin;
  }

/**
   * Asegura que tenemos un Player ID de OneSignal (espera a que el SDK lo proporcione)
   */
  private ensurePlayerId(): Observable<string> {
    return new Observable(observer => {
      if (!this.canInitializeOneSignal()) {
        observer.error(new Error('OneSignal deshabilitado fuera del dominio productivo'));
        return;
      }

      if (this.playerId) {
        observer.next(this.playerId);
        observer.complete();
        return;
      }

      const w = window as any;
      w.OneSignal = w.OneSignal || [];
      const OneSignalRef = w.OneSignal;

      let timeoutId: any = null;

      // Timeout de seguridad por si OneSignal nunca responde
      timeoutId = setTimeout(() => {
        if (!this.playerId) {
          observer.error(new Error('OneSignal Player ID no disponible'));
        }
      }, 15000);

      OneSignalRef.push(() => {
        OneSignalRef.getUserId((userId: string) => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          if (userId) {
            this.playerId = userId;
            console.log('OneSignal Player ID (ensure):', this.playerId);
            this.updateDeviceStatus();
            observer.next(userId);
            observer.complete();
          } else {
            observer.error(new Error('OneSignal Player ID no disponible'));
          }
        });
      });
    });
  }

  /**
   * Registra el dispositivo para recibir notificaciones
   */
  registerDevice(userId: number, deviceType: string = 'web'): Observable<any> {
    return new Observable(observer => {
      this.ensurePlayerId().subscribe({
        next: (playerId: string) => {
          const data = {
            onesignal_player_id: playerId,
            id_usuario: userId,
            dispositivo_tipo: deviceType,
            idempresa: (this.httpService as any).idempresa || 1
          };

          this.http.post(
            (this.httpService as any).urlbase + 'ware/notificaciones/registrar_dispositivo.php',
            data
          ).subscribe(
            (response: any) => {
              if (response.code === 1) {
                this.deviceRegistered$.next(true);
                observer.next(response);
                observer.complete();
              } else {
                observer.error(response);
              }
            },
            (error) => {
              observer.error(error);
            }
          );
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

/**
   * Verifica si el usuario tiene algún dispositivo registrado
   */
  checkDeviceRegistration(userId: number): Observable<any> {
    const data = {
      id_usuario: userId,
      idempresa: (this.httpService as any).idempresa || 1
    };

    return this.http.post(
      (this.httpService as any).urlbase + 'ware/notificaciones/check_dispositivo.php',
      data
    );
  }

  /**
   * Obtiene el player ID actual
   */
  getPlayerId(): string | null {
    return this.playerId;
  }

/**
   * Permite que otros componentes sincronicen el estado desde el servidor
   */
  public setDeviceRegisteredFromServer(isRegistered: boolean): void {
    this.deviceRegistered$.next(isRegistered);
  }

/**
   * Desregistra el dispositivo actual para dejar de recibir notificaciones
   */
  unregisterDevice(userId: number): Observable<any> {
    const data: any = {
      id_usuario: userId,
      idempresa: (this.httpService as any).idempresa || 1,
    };

    // Si conocemos el playerId actual, lo incluimos para desactivar sólo ese dispositivo
    if (this.playerId) {
      data.onesignal_player_id = this.playerId;
    }

    return new Observable(observer => {
      this.http.post(
        (this.httpService as any).urlbase + 'ware/notificaciones/desactivar_dispositivo.php',
        data
      ).subscribe(
        (response: any) => {
          if (response.code === 1) {
            this.deviceRegistered$.next(false);
            observer.next(response);
            observer.complete();
          } else {
            observer.error(response);
          }
        },
        (error) => {
          observer.error(error);
        }
      );
    });
  }

  /**
   * Envía una notificación push
   */
  sendPushNotification(
    titulo: string,
    mensaje: string,
    destino: string = 'todos', // 'todos', 'usuario_id', 'player_id'
    destino_valor?: any,
    icono?: string,
    imagen?: string
  ): Observable<any> {
    const data = {
      titulo: titulo,
      mensaje: mensaje,
      destino: destino,
      destino_valor: destino_valor,
      icono: icono,
      imagen: imagen,
      idempresa: (this.httpService as any).idempresa || 1
    };

    return this.http.post(
      (this.httpService as any).urlbase + 'ware/notificaciones/enviar_notificacion.php',
      data
    );
  }

  /**
   * Maneja notificaciones recibidas
   */
  onPushReceived(callback: (notification: any) => void): void {
    if (typeof OneSignal !== 'undefined') {
      OneSignal.on('notificationDisplay', callback);
    }
  }
}
